import { useToast } from '@mdvip/shared/hooks/use-toast';
import { useErrorTranslation } from '@mdvip/shared/hooks/useErrorTranslation';
import { useInFlightIds } from '@mdvip/shared/hooks/useInFlightIds';
import { extractGqlErrorCode } from '@mdvip/shared/lib/gqlErrors';
import { MDVIP_COMPANY_INFO } from '@mdvip/shared/lib/mdvip-company-info';
import { trackException } from '@mdvip/shared/lib/telemetry';
import { type UseMutationResult, useMutation } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { PaymentReceiptEmailError, type ReceiptEmailResult, requestSendPaymentReceiptEmail } from './receiptsApi';

interface UseSendPaymentReceiptEmailOptions {
  onSuccess?: (result: ReceiptEmailResult) => void;
}

/**
 * Emails the member a receipt for one past payment. A one-shot action: no query key, no cache
 * invalidation — nothing the member can see changes as a result.
 *
 * Owns its own error toast so the Retry action can re-fire this exact operation. The title is the
 * flow's own copy rather than the app-wide generic `tError('title')` — a deliberate exception for
 * this flow.
 *
 * The explicit `UseMutationResult` annotation is required: `onError` refers back to `mutation` to
 * implement Retry, which TypeScript cannot infer through.
 */
export function useSendPaymentReceiptEmail({ onSuccess }: UseSendPaymentReceiptEmailOptions = {}) {
  const { toast } = useToast();
  const { t } = useTranslation('members');
  const tError = useErrorTranslation();
  const inFlight = useInFlightIds();

  const mutation: UseMutationResult<ReceiptEmailResult, unknown, string> = useMutation({
    mutationFn: (paymentId: string) => requestSendPaymentReceiptEmail(paymentId),
    // Every invocation passes through here, including a Retry fired from the error toast.
    onMutate: (paymentId: string) => {
      inFlight.add(paymentId);
    },
    onSettled: (_result, _error, paymentId: string) => {
      inFlight.remove(paymentId);
    },
    onSuccess,
    onError: (error: unknown, paymentId: string) => {
      // PaymentReceiptEmailError wraps the Failure variant (NOT_FOUND, FAILED). The wire value goes
      // to telemetry; i18n looks it up prefixed, since bare NOT_FOUND is a generic message.
      const isDomainError = error instanceof PaymentReceiptEmailError;
      const code = isDomainError ? error.code : extractGqlErrorCode(error);
      const errorKey = isDomainError ? `PAYMENT_RECEIPT_${code}` : code;
      if (error instanceof Error) {
        trackException(error, { source: 'sendPaymentReceiptEmail', errorCode: code });
      }
      toast({
        title: t('payments.transactions.receipt.errorTitle'),
        description: tError(errorKey, { phone: MDVIP_COMPANY_INFO.memberSupportPhone }),
        variant: 'destructive',
        // Infinite duration needs an explicit way out, so the dismiss button is not optional here.
        duration: Number.POSITIVE_INFINITY,
        closeButton: true,
        action: { label: t('payments.transactions.receipt.retry'), onClick: () => mutation.mutate(paymentId) },
      });
    },
  });

  // `pendingIds` rather than the mutation's own `isPending`: that boolean only describes the most
  // recent call, so it cannot answer which rows are busy once two are running.
  return { submit: (paymentId: string) => mutation.mutate(paymentId), pendingIds: inFlight.ids };
}
